import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { ArrowLeft, Mail, CheckCircle } from 'lucide-react';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { forgotPassword } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await forgotPassword(email);

    if (result.success) {
      setSubmitted(true);
    } else {
      toast({
        title: 'Erreur',
        description: result.error,
        variant: 'destructive',
      });
    }

    setLoading(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-pcc-background py-12 px-4">
        <Card className={`w-full max-w-md border-0 shadow-xl ${'bg-pcc-card border border-pcc-card-border'}`}>
          <CardContent className="pt-8 text-center">
            <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${'bg-pcc-teal-soft'}`}>
              <CheckCircle size={32} className="text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-pcc-text mb-2">Email envoyé!</h2>
            <p className="text-pcc-text-muted mb-6">
              Si un compte existe avec l&apos;adresse <strong>{email}</strong>, vous recevrez un email 
              avec les instructions pour réinitialiser votre mot de passe.
            </p>
            <Button
              onClick={() => navigate('/login')}
              className="w-full h-12 bg-pcc-primary hover:bg-pcc-primary-dark text-white rounded-xl"
            >
              Retour à la connexion
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-pcc-background py-12 px-4">
      <Card className={`w-full max-w-md border-0 shadow-xl ${'bg-pcc-card border border-pcc-card-border'}`}>
        <CardHeader className="space-y-1">
          {/* Back button */}
          <button
            onClick={() => navigate(-1)}
            className="absolute top-[max(env(safe-area-inset-top),12px)] left-4 p-2 text-pcc-text-muted hover:text-pcc-text transition-colors"
          >
            <ArrowLeft size={20} />
          </button>

          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 bg-pcc-primary/10 rounded-2xl flex items-center justify-center">
              <Mail size={32} className="text-pcc-primary" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-center text-pcc-text">
            Mot de passe oublié?
          </CardTitle>
          <CardDescription className="text-center text-pcc-text-muted">
            Entrez votre email pour recevoir un lien de réinitialisation
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-password-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="votre@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12 rounded-xl"
                data-testid="forgot-email"
              />
            </div>
            <Button
              type="submit"
              className="w-full h-12 bg-pcc-primary hover:bg-pcc-primary-dark text-white rounded-xl font-semibold"
              disabled={loading}
              data-testid="forgot-submit"
            >
              {loading ? 'Envoi en cours...' : 'Envoyer le lien'}
            </Button>
          </form>
          
          <div className="mt-6 text-center text-sm">
            <Link to="/login" className="text-pcc-primary hover:underline font-semibold">
              Retour à la connexion
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPassword;
